import React from 'react';
import { Clock, TrendingUp, TrendingDown, Award } from 'lucide-react';

const ResponseTimeMetrics = ({ metrics, loading }) => {
  const getPerformanceColor = (avgTime) => {
    if (avgTime <= 60) return 'text-green-600 bg-green-100';
    if (avgTime <= 120) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getPerformanceIndicator = (avgTime) => {
    if (avgTime <= 60) return { icon: Award, color: 'text-green-600', label: 'Excellent' };
    if (avgTime <= 120) return { icon: TrendingUp, color: 'text-yellow-600', label: 'Good' };
    return { icon: TrendingDown, color: 'text-red-600', label: 'Needs Improvement' };
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Team Response Time Performance</h2>
        <p className="text-sm text-gray-600 mt-1">Average response times by team member (last 30 days)</p>
      </div>
      <div className="p-4">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading metrics...</div>
        ) : metrics?.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No response data available</div>
        ) : (
          <div className="space-y-3">
            {metrics?.map((member, index) => {
              const performance = getPerformanceIndicator(member?.avgResponseTime);
              const PerformanceIcon = performance?.icon;

              return (
                <div
                  key={member?.userId}
                  className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1">
                    {/* Rank Badge */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      index === 0 ? 'bg-yellow-100 text-yellow-700' :
                      index === 1 ? 'bg-gray-200 text-gray-700' :
                      index === 2 ? 'bg-orange-100 text-orange-700': 'bg-gray-100 text-gray-600'
                    }`}>
                      {index + 1}
                    </div>

                    {/* Member Info */}
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{member?.fullName}</p>
                      <p className="text-sm text-gray-500">{member?.email}</p>
                    </div>
                  </div>
                  {/* Response Stats */}
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className={`text-lg font-bold ${getPerformanceColor(member?.avgResponseTime)?.split(' ')?.[0]}`}>
                          {member?.avgResponseTime}m
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {member?.totalResponses} response{member?.totalResponses !== 1 ? 's' : ''}
                      </p>
                    </div>

                    {/* Performance Indicator */}
                    <div className={`p-2 rounded-lg ${getPerformanceColor(member?.avgResponseTime)}`}>
                      <PerformanceIcon className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Performance Legend */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <p className="text-sm font-medium text-gray-700 mb-2">Performance Indicators:</p>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-gray-600">Excellent (&lt;60m)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <span className="text-gray-600">Good (60-120m)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span className="text-gray-600">Needs Improvement (&gt;120m)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponseTimeMetrics;